/**
 * This file is the entry point for the React app, it sets up the root
 * element and renders the App component to the DOM.
 *
 * It is included in `src/index.html`.
 */

import { createRoot } from "react-dom/client";
import { GoogleOAuthProvider } from '@react-oauth/google';
import App from "./App";
import { getEnv } from './utils/env';

// Get Google Client ID from environment variables with fallback
// IMPORTANT: If you use a custom Google OAuth client, set `VITE_GOOGLE_CLIENT_ID`
// or `BUN_PUBLIC_GOOGLE_CLIENT_ID` in your environment. The fallback below
// has been updated to match the client ID you provided so frontend and backend
// use the same default during development.
const GOOGLE_CLIENT_ID = getEnv('VITE_GOOGLE_CLIENT_ID', '659625383784-m21jkqgi4i645a9h1lnqm8jrl17i4c84.apps.googleusercontent.com') ||
  getEnv('BUN_PUBLIC_GOOGLE_CLIENT_ID', '659625383784-m21jkqgi4i645a9h1lnqm8jrl17i4c84.apps.googleusercontent.com');

// Debug: Log the client ID to verify it's loading


// Firebase SDK v12 uses AbortController internally for its WebChannel / token-refresh
// fetches. When those connections are torn down during navigation or HMR the abort
// sometimes propagates as an unhandled rejection before Firebase's own catch is
// registered. Suppress those here so they don't trigger the Bun error overlay.
window.addEventListener('unhandledrejection', (event) => {
  if (event.reason?.name === 'AbortError') {
    event.preventDefault();
  }
});

// This app NEVER registers a service worker. If a stale or rogue page-scoped SW is
// present — left by an old build, or injected by a browser extension into the page
// origin — unregister it and clear its caches. Such a SW can intercept /api/ fetches
// and drop the Authorization header, silently 401-ing every admin request (the
// "admin shows no data" symptom). Runs before the app mounts.
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations()
    .then((regs) => { if (regs.length) regs.forEach((r) => r.unregister()); })
    .catch(() => { /* ignore */ });
  try {
    if (typeof caches !== 'undefined' && caches.keys) {
      caches.keys().then((keys) => keys.forEach((k) => caches.delete(k))).catch(() => {});
    }
  } catch { /* ignore */ }
}

function start() {
  const root = createRoot(document.getElementById("root")!);

  root.render(
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <App />
    </GoogleOAuthProvider>
  );
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", start);
} else {
  start();
}
